import { IBaseAddress, IBaseSocialMedia } from "@carm/shared/components/form-elements/models";
import { AddressCodeType, IBusinessAddress, Business } from "@carm/_core";
import { IBaseAddressResponse, IBaseSocialMediaResponse } from "@carm/_core/access-control-mgt/models/address-api.model";
import { IProgramAddress, IContactSubmitModel, AddressType, ISameAsAddressModel, IContact, IProgramAddressSubmitModel, IBusinessRegistrationDraft } from "@carm/_core/business-registration/models";
import { IProgramEnrolmentSaveMediaRequest, IProgramEnrolmentDraftRequest, IHaveCarrierInformation, IThirdPartyDraftRequest, IEdiDraftRequest, ISocialMediaSubmitModel, IProgramEnrolmentDraftResponse } from "@carm/_core/program-enrolment/models/enrol-program-api.model";
import { ProgramEnrolmentType, EnrolmentType, CarrierSubProgramType, ThirdPartySubProgramType, ThirdPartyForeignEntityType, EdiServiceProviderCategoryType, CheckedOptions, thirdPartyTypeToS4EnrolmentTypesMap, thirdPartyS4EdiServiceTypeToThirdPartyEdiServiceType } from "@carm/_core/program-enrolment/models/enrol-program-enums.model";
import { IUploadEnrolmentModel, ICarrierEnrolmentModel, IImporterExporterThirdPartyEnrolmentModel, ISocialMediaAddress } from "@carm/_core/program-enrolment/models/enrol-program.model";
import { TranslateService } from "@ngx-translate/core";
import * as dayjs from "dayjs";
import { IProgramEnrolmentBusinessInfo } from "../models/program-enrolment-business-info";


export function mapEnrolmentModelToSaveMediaRequest(
  model:
    | IUploadEnrolmentModel
    | ICarrierEnrolmentModel
    | IImporterExporterThirdPartyEnrolmentModel,
  draftId: string,
  programType: ProgramEnrolmentType
): IProgramEnrolmentSaveMediaRequest {
  return {
    ...model,
    draftId,
    programType,
  } as IProgramEnrolmentSaveMediaRequest;
}

export function mapEnrolmentModelToSubmitRequest(
  model:
    | IUploadEnrolmentModel
    | ICarrierEnrolmentModel
    | IImporterExporterThirdPartyEnrolmentModel,
  draftId: string,
  programType: ProgramEnrolmentType
): IProgramEnrolmentDraftRequest {
  switch (programType) {
    case ProgramEnrolmentType.Warehouse:
    case ProgramEnrolmentType.DutyFreeShop:
    case ProgramEnrolmentType.CustomsBroker:
      const uploadModel = model as IUploadEnrolmentModel;
      return mapUploadEnrolmentToDraftRequest(
        uploadModel,
        draftId,
        programType
      );
    case ProgramEnrolmentType.CarrierFreightForwarder:
      const carrierModel = model as ICarrierEnrolmentModel;
      return mapCarrierEnrolmentToDraftRequest(
        carrierModel,
        draftId,
        programType
      );
    case ProgramEnrolmentType.Importer:
    case ProgramEnrolmentType.ThirdParty:
    case ProgramEnrolmentType.Exporter:
      const exporterModel = model as IImporterExporterThirdPartyEnrolmentModel;
      return mapImporterExporterThirdPartyEnrolmentToDraftRequest(
        exporterModel,
        draftId,
        programType
      );
  }
}

function mapUploadEnrolmentToDraftRequest(
  model: IUploadEnrolmentModel,
  draftId: string,
  programType: ProgramEnrolmentType
): IProgramEnrolmentDraftRequest {
  const modelToSubmit = {
    ...model,
    draftId,
    programType,
    enrolmentType: model.enrolmentType,
    warehouseType: model.programSubType || undefined,
    region: model.region || undefined,
    port: model.portLocation || undefined
  } as IProgramEnrolmentDraftRequest;
  return modelToSubmit;
}

function mapCarrierEnrolmentToDraftRequest(
  carrierModel: ICarrierEnrolmentModel,
  draftId: string,
  programType: ProgramEnrolmentType
): IProgramEnrolmentDraftRequest {
  const carrierAirModel = carrierModel.otherInfo?.air;
  const carrierHighwayModel = carrierModel?.otherInfo?.highway;
  const carrierMarineModel = carrierModel?.otherInfo?.marine;
  const carrierFreightFowarderModel = carrierModel?.otherInfo?.freightForwarder;
  const carrierRailModel = carrierModel?.otherInfo?.rail;
  const addressesModel = [
    {
      ...carrierModel.address?.mailingAddress,
      addressType: AddressCodeType.MAILING_A,
    } as IProgramAddress,
    {
      ...carrierModel.address?.physicalAddress,
      addressType: AddressCodeType.BUSINESS_A,
    } as IProgramAddress,
    {
      ...carrierModel.address?.recordsAddress,
      addressType: AddressCodeType.BKSRCRDS_A,
    } as IProgramAddress,
    ...(carrierModel.address?.additionalAddresses as IProgramAddress[]),
  ];
  const modelToSubmit = {
    draftId,
    programType,
    enrolmentType: EnrolmentType.New,
    carrierType: carrierModel.details?.subProgramType,
    programAccountName: carrierModel.details?.programAccountName,
    booksRecordsInCanada: carrierModel?.address?.booksKeptInCanada === true ? CheckedOptions.Checked : CheckedOptions.UnChecked,
    booksRecordsAtBusiness: carrierModel?.address?.booksKeptInPlaceOfBusiness === true ? CheckedOptions.Checked : CheckedOptions.UnChecked,
    addresses: addressesModel
      ?.filter(c => !!c.line1)
      ?.map(mapProgramAddressToSubmitRequest),
    contacts: carrierModel.contact?.contacts
      ?.filter(c => !!c.firstName)
      ?.map(mapContactToSubmitRequest),
    carrier: {
      carrierType: carrierModel.details?.subProgramType,
      // highway or freight forwarder
      requireSharedSecret:
        carrierModel.details?.subProgramType === CarrierSubProgramType.Highway
          ? carrierHighwayModel?.sharedSecreteAccessCodes
          : carrierFreightFowarderModel?.sharedSecreteAccessCodes,
      // marine
      imoIdNumber: carrierMarineModel?.haveImoNumber
        ? carrierMarineModel?.imoNumber
        : undefined,
      nbEquipment:
        carrierModel.details?.subProgramType === CarrierSubProgramType.Highway
          ? carrierHighwayModel?.numberOfEquipment
          : carrierMarineModel?.numberOfVessels,
      // air
      airIataCode: carrierAirModel?.iataCode,
      makeModelLargestAircraft: carrierAirModel?.makeModelOfLargestAircraft,
      nbSmallPassAircrafts: carrierAirModel?.numberOfSmallAircrafts,
      nbMediumPassAircrafts: carrierAirModel?.numberOfMediumAircrafts,
      nbLargePassAircrafts: carrierAirModel?.numberOfLargeAircrafts,
      nbLargeCargoAircrafts: carrierAirModel?.numberOfLargeCargoAircrafts,
      nbSmallMedCargoAircrafts:
        carrierAirModel?.numberOfSmallOrMediumCargoAircrafts,
      // rail
      ownLocomotive: carrierRailModel?.ownOperateLocomotive,
    } as IHaveCarrierInformation,
    socialMedias: carrierModel.address.socialMediaAddresses
      .filter(c => !!c.socialMediaType)
      ?.map(mapSocialMediaToSubmitRequest),
    certification: carrierModel.certification?.certification,
  } as IProgramEnrolmentDraftRequest;
  return modelToSubmit;
}

function mapImporterExporterThirdPartyEnrolmentToDraftRequest(
  modelToEnrol: IImporterExporterThirdPartyEnrolmentModel,
  draftId: string,
  programType: ProgramEnrolmentType
): IProgramEnrolmentDraftRequest {
  const addressesModel = [
    {
      ...modelToEnrol.address?.mailingAddress,
      addressType: AddressCodeType.MAILING_A,
    } as IProgramAddress,
    {
      ...modelToEnrol.address?.physicalAddress,
      addressType: AddressCodeType.BUSINESS_A,
    } as IProgramAddress,
    {
      ...modelToEnrol.address?.recordsAddress,
      addressType: AddressCodeType.BKSRCRDS_A,
    } as IProgramAddress,
    ...(modelToEnrol.address?.additionalAddresses as IProgramAddress[]),
  ];
  if (modelToEnrol?.contact?.contacts) {
    modelToEnrol.contact.contacts.forEach(contact =>
      {contact.telephone = contact.telIntFormGroup.e164Phone});
  }
  const contactsModel = [
    ...modelToEnrol.contact?.contacts,
    ...[modelToEnrol.contact?.contactIt], // todo: might need to be removed
  ];
  const isThirdParty = programType === ProgramEnrolmentType.ThirdParty;
  const modelToSubmit = {
    draftId,
    programType,
    enrolmentType: EnrolmentType.New,
    importerType: isThirdParty
      ? undefined
      : modelToEnrol.details?.subProgramType,
    thirdParty: isThirdParty
      ? ({
          hasApi: modelToEnrol.details?.setAPIWithCBSA === true,
          type3rdParty: modelToEnrol.details?.subProgramType,
          type3rdPartyEdi:
            modelToEnrol.details?.subProgramType ===
            ThirdPartySubProgramType.EDIServiceProvider,
          type3rdPartyExporter: modelToEnrol.details?.foreignEntityTypes?.includes(
            ThirdPartyForeignEntityType.Exporter
          ),
          type3rdPartyMarine: modelToEnrol.details?.foreignEntityTypes?.includes(
            ThirdPartyForeignEntityType.MarineAgent
          ),
          type3rdPartyProducer: modelToEnrol.details?.foreignEntityTypes?.includes(
            ThirdPartyForeignEntityType.Producer
          ),
          type3rdPartyVendor: modelToEnrol.details?.foreignEntityTypes?.includes(
            ThirdPartyForeignEntityType.Vendor
          ),
          type3rdPartyShipping: modelToEnrol.details?.foreignEntityTypes?.includes(
            ThirdPartyForeignEntityType.ShippingAgent
          ),
        } as IThirdPartyDraftRequest)
      : undefined,
    edi: modelToEnrol.details?.subProgramType === ThirdPartySubProgramType.EDIServiceProvider
      ? ({
          ediConnection: modelToEnrol.details?.ediServiceProviderCategories?.includes(
            EdiServiceProviderCategoryType.EdiConnection
          )
            ? CheckedOptions.Checked
            : CheckedOptions.UnChecked,
          ediSoftware: modelToEnrol.details?.ediServiceProviderCategories?.includes(
            EdiServiceProviderCategoryType.EdiSoftware
          )
            ? CheckedOptions.Checked
            : CheckedOptions.UnChecked,
          ediConnectionMethod: modelToEnrol.details?.connectionType,
          requestNewConnection: modelToEnrol.details?.requestConnectionType
            ? CheckedOptions.Checked
            : CheckedOptions.UnChecked,
          ediService: thirdPartyTypeToS4EnrolmentTypesMap.get(modelToEnrol.details?.ediServiceType),
        } as IEdiDraftRequest)
      : undefined,
    programAccountName: modelToEnrol.details?.programAccountName,
    addresses: addressesModel
      ?.filter(c => !!c.line1)
      ?.map(mapProgramAddressToSubmitRequest),
    contacts: contactsModel?.filter(c => !!c)?.map(mapContactToSubmitRequest),
    socialMedias: modelToEnrol.address.socialMediaAddresses
      .filter(c => !!c.socialMediaType)
      ?.map(mapSocialMediaToSubmitRequest),
    certification: modelToEnrol.certification?.certification,
    booksRecordsInCanada: programType !== ProgramEnrolmentType.Importer && programType !== ProgramEnrolmentType.Exporter ? undefined :
                     modelToEnrol?.address?.booksKeptInCanada === true ? CheckedOptions.Checked : CheckedOptions.UnChecked,
    booksRecordsAtBusiness: programType !== ProgramEnrolmentType.Importer && programType !== ProgramEnrolmentType.Exporter ? undefined :
                     modelToEnrol?.address?.booksKeptInPlaceOfBusiness === true ? CheckedOptions.Checked : CheckedOptions.UnChecked,
  } as IProgramEnrolmentDraftRequest;
  return modelToSubmit;
}

function mapContactToSubmitRequest(model: IContact): IContactSubmitModel {
  return {
    email: model?.email,
    contactType: model?.contactType || 'Z099', // todo: other -> to create enum
    otherContactType: model?.otherContactType,
    extension: model?.extension,
    title: model?.contactTitle,
    otherContactTitle: model?.otherContactTitle,
    fax: model?.fax?.replace(/-/g, ''),
    firstName: model?.firstName,
    lastName: model?.lastName,
    language: model?.languagePref,
    telephone: model?.telIntFormGroup?.e164Phone ? model?.telIntFormGroup?.e164Phone : model?.telephone?.removeHyphens(),
  } as IContactSubmitModel;
}

export function mapProgramAddressToSubmitRequest(
  model: IProgramAddress
): IProgramAddressSubmitModel {
  return {
    addressType: model?.addressType,
    careOf: model?.careOf,
    line1: model?.line1,
    line2: model?.line2,
    town: model?.town,
    poBox: model?.poBox?.toString(),
    country: model?.carmCountry,
    postalCode: model?.postalCode?.toUpperCase(),
    region: model?.carmRegion,
    phone: model?.telIntFormGroup?.e164Phone,
    phoneExt: model?.extension,
    brAddressRemark: model?.brAddressRemark,
    faxNumber: model?.fax?.replace(/-/g, ''),
    languagePreference: model?.languagePref,
    addressId: model?.addressId,
  } as IProgramAddressSubmitModel;
}

export function mapBaseAddressToSubmitRequest(
  from: IBaseAddress
): IBaseAddressResponse {
  return {
    addressId: from?.addressId,
    addressIdByExternalSystem: from?.addressIdByExternalSystem,
    addressType: from?.addressType,
    brAddressRemark: from?.brAddressRemark,
    careOf: from?.careOf,
    cellPhone: from?.cellPhone,
    correspondencePreference: from?.correspondencePreference,
    country: from?.country,
    email: from?.email,
    faxNumber: from?.faxNumber?.replace(/-/g, ''),
    languagePreference: from?.languagePreference,
    line1: from?.line1,
    line2: from?.line2,
    phone: from?.telIntFormGroup?.e164Phone,
    phoneExt: from?.phoneExt,
    poBox: from?.poBox,
    postalCode: from?.postalCode?.toUpperCase(),
    region: from?.region,
    town: from?.town,
  } as IBaseAddressResponse;
}

export function mapBaseAddressToProgramAddress(
  from: IBaseAddress
): IProgramAddress {
  return {
    uid: from.addressId,
    carmCountry: from.country,
    line1: from.line1,
    line2: from.line2,
    poBox: from.poBox ? parseInt(from.poBox, 10) : '',
    postalCode: from.postalCode,
    carmRegion: from.region,
    town: from.town,
    phone: from.phone,
    careOf: from.careOf,
    addressType: from.addressType,
    telephone: from.phone,
    fax: from.faxNumber,
    extension: from.phoneExt,
    languagePref: from.languagePreference,
  } as IProgramAddress;
}

export function mapBusinessAddressToSubmitRequest(
  model: IBusinessAddress
): IProgramAddressSubmitModel {
  return {
    addressIdByExternalSystem: model?.addressIdByExternalSystem,
    addressId: model?.uid,
    addressType: model?.addressType,
    careOf: model?.careOf,
    line1: model?.line1,
    line2: model?.line2,
    country: model?.carmCountry,
    region: model?.carmRegion,
    town: model?.town,
    postalCode: model?.postalCode?.toUpperCase(),
    poBox: model?.poBox?.toString(),
    phone: model?.phone?.removeHyphens(),
  } as IProgramAddressSubmitModel;
}

export function mapSocialMediaToSubmitRequest(
  model: ISocialMediaAddress
): ISocialMediaSubmitModel {
  return {
    socialMediaType: model?.socialMediaType,
    otherSocialMediaType: model?.otherSocialMediaType,
    socialMediaAddress: model?.socialMediaAddress,
  } as ISocialMediaSubmitModel;
}

export function mapBaseSocialMediaToSubmitRequest(
  model: IBaseSocialMedia
): IBaseSocialMediaResponse {
  return {
    addressId: model?.addressId,
    addressIdByExternalSystem: model?.addressIdByExternalSystem,
    addressType: model?.addressType,
    socialMediaType: model?.socialMediaType,
    otherSocialMediaType: model?.otherSocialMediaType,
    socialMediaAddress: model?.socialMediaAddress,
  } as IBaseSocialMediaResponse;
}

export const mapResponseToProgramEnrolmentStatus = (
  from: IProgramEnrolmentDraftResponse,
  translateService: TranslateService
): IProgramEnrolmentDraftResponse => {
  const subType = from.subType || from.warehouseType || from.carrierType || from.importerType || from.thirdPartyType;
  const programTypeKey = `${from.programType}_${subType}`.toLowerCase().replace(/ /g, '_');
  const isProgramWithSubType = !!from.programType && !!subType &&
  (from.programType === ProgramEnrolmentType.CarrierFreightForwarder ||
   from.programType === ProgramEnrolmentType.Warehouse ||
   from.programType === ProgramEnrolmentType.ThirdParty);

  const thirdPartyName = from.programType === ProgramEnrolmentType.ThirdParty && isProgramWithSubType
                         ? translateService.instant(`program_account.profile.program_types.${from.programType}`) + ` (${subType})` : null;
  const typeName = isProgramWithSubType
    ? thirdPartyName ?? translateService.instant(`program_account.profile.program_types_with_subtype.${programTypeKey}`)
    : translateService.instant(`program_account.profile.program_types.${from.programType}`);

  return {
    bn15: from?.bn15,
    caseNumber: from?.caseNumber,
    draftMedia: from.draftMedia,
    lastModifiedDate: !!from.lastModifiedDate
      ? dayjs(from.lastModifiedDate).format('YYYY-MM-DD')
      : '',
    draftName: from.draftName,
    programAccountName: from.programAccountName,
    programAccountNumber: from.programAccountNumber,
    statusMsg: from.statusMsg,
    statusCode: from.statusCode,
    requiredBondAmount: from.requiredBondAmount,
    licenseFee: from.licenseFee,
    enrolmentType: from.enrolmentType
      ? translateService.instant(
          'program_account.program_list.details.requests.enrolmentType.' +
            from.enrolmentType
        )
      : translateService.instant(
          'program_account.program_list.details.requests.enrolmentType.New'
        ),
    submittedDate: !!from.submittedDate
      ? dayjs(from.submittedDate).format('YYYY-MM-DD')
      : '',
    programType: from.programType,
    typeName,
    draftId: from.draftId,
    subType: subType,
    originalSubType: from.warehouseType || from.carrierType || from.importerType || from.thirdPartyType,
    subTypeName: translateService.instant(
      `sub-program-enrolment.requirements.sub-program-title.${from.subType}`
    ),
    ediServiceType: thirdPartyS4EdiServiceTypeToThirdPartyEdiServiceType.get(from?.ediServiceType),
    caseType: from.caseType,
    appealPermitted: from.appealPermitted,
    hybrisCaseStatus: from.hybrisCaseStatus,
    isProgramWithSubType
  } as IProgramEnrolmentDraftResponse;
};

export function mapSimilarBaseAddresses(
  addresses: IBaseAddressResponse[],
  excludeAddress: AddressType,
  translateService: TranslateService
): ISameAsAddressModel[] {
  const sameAsAddresses: ISameAsAddressModel[] = [];
  addresses?.filter(c => !!c.line1 && !!c.country && !!c.town)?.forEach(address => {
    if (
      excludeAddress !== getAddressType(address.addressType as AddressCodeType)
    ) {
      sameAsAddresses.push({
        address: mapBaseAddressToProgramAddress(address),
        addressType: getAddressType(address.addressType as AddressCodeType),
        name: translateService.instant('view_address_fieldset.address'),
        overrideName: false
      });
    }
  });
  return sameAsAddresses;
}

function getAddressType(addressType: AddressCodeType): AddressType {
  switch (addressType) {
    case AddressCodeType.BUSINESS_A:
    case AddressCodeType.BUSINESS_P:
      return AddressType.Physical;
    case AddressCodeType.MAILING_A:
    case AddressCodeType.MAILING_P:
    case AddressCodeType.ASEC:
    case AddressCodeType.XXDEFAULT:
    case AddressCodeType.ACCPYBLE_A:
    case AddressCodeType.PLANT_A:
      return AddressType.Mailing;
    case AddressCodeType.BKSRCRDS_A:
    case AddressCodeType.BKSRCRDS_P:
      return AddressType.Records;
    default:
      return AddressType.Generic;
  }
}

export const mapToProgramEnrolmentBusinessInfo = (business: Business) => {
  return {
    businessNumber: business.uid,
    legalEntityName: business.legalName,
    operatingName: business.operatingName,
  } as IProgramEnrolmentBusinessInfo;
};

export const mapRegistrationStatusToProgramEnrolmentBusinessInfo = (registrationStatus: IBusinessRegistrationDraft): IProgramEnrolmentBusinessInfo => {
  return {
    legalEntityName: registrationStatus?.businessLegalName,
    operatingName: registrationStatus?.businessOperatingName,
    businessNumber: registrationStatus?.bn9,
    scenario: registrationStatus?.scenario,
    businessDraftId: registrationStatus?.uid,
  } as IProgramEnrolmentBusinessInfo;
};
